import { Box, Button, Spinner, useBelowBreakpoint, type BoxProps, type ButtonProps } from '@stitch-fix/mode-react';
import React from 'react';
import NextLink from 'next/link';
import classNames from 'classnames';
import styles from './styles.module.scss';
type ContinueButtonProps = Omit<ButtonProps, 'width'> & {
  width?: BoxProps['width'];
};
export interface ActionBarProps {
  isContinueShown?: boolean;
  isSkipShown?: boolean;
  onContinueClick?: () => void;
  onSkipClick?: () => void;
  skipHref?: string;
  skipButtonText?: string;
  continueButtonProps?: ContinueButtonProps;
  continueButtonCentered?: boolean;
  continueButtonText?: string;
  isSticky?: boolean;
  isLoading?: boolean;
  skipButtonAsNextLink?: boolean;
  tertiaryButtonText?: string;
  tertiaryButtonVariant?: ButtonProps['variant'];
  isTertiaryShown?: boolean;
  onTertiaryClick?: () => void;
}
const getContinueButtonWidth = ({
  isXSMobile,
  isSkipShown,
  continueButtonCentered,
  width
}: {
  isXSMobile: boolean;
  isSkipShown: boolean;
  continueButtonCentered: boolean;
  width: ContinueButtonProps['width'];
}) => {
  if (isXSMobile) {
    return '165px';
  }
  if (isSkipShown) {
    return '217px';
  }
  if (continueButtonCentered) {
    return '344px';
  }
  return width;
};
export const ActionBar = ({
  isContinueShown = true,
  isSkipShown = true,
  onContinueClick,
  onSkipClick,
  skipHref,
  continueButtonCentered = false,
  continueButtonProps = {},
  continueButtonText,
  isSticky,
  isLoading,
  skipButtonAsNextLink = true,
  skipButtonText,
  tertiaryButtonText,
  tertiaryButtonVariant = 'text',
  isTertiaryShown = false,
  onTertiaryClick
}: ActionBarProps) => {
  const isXSMobile = useBelowBreakpoint(360);
  const {
    width = '200px',
    ...continueProps
  } = continueButtonProps;
  const adjustedWidth = getContinueButtonWidth({
    isXSMobile,
    isSkipShown,
    continueButtonCentered,
    width
  });
  const showTertiaryButton = isTertiaryShown && Boolean(tertiaryButtonText);
  return <Box height={showTertiaryButton ? '150px' : '92px'}>
      <Box className={classNames(styles.container, {
      [styles.sticky]: isSticky,
      [styles.wrap]: showTertiaryButton
    })}>
        {isSkipShown && <Box className={styles.skipButton}>
            {skipButtonAsNextLink && skipHref ? <Button as={NextLink} onClick={onSkipClick} variant="outline-large" href={skipHref} disabled={isLoading}
        // If a page prefetches the next page, and a new deploy is made,
        // the props on the next page are empty so we need to disable prefetching
        // Related issue: https://github.com/vercel/next.js/pull/60968
        prefetch={false}>
                {skipButtonText ?? 'Skip'}
              </Button> : <Button onClick={onSkipClick} variant="outline-large" disabled={isLoading}>
                {skipButtonText ?? 'Skip'}
              </Button>}
          </Box>}
        {isContinueShown && <Box className={classNames(styles.continueButton, {
        [styles.centered]: continueButtonCentered
      })} width={adjustedWidth}>
            <Button onClick={onContinueClick} width="fill" type="submit" variant="filled-statement-large" disabled={isLoading} endIcon={isLoading ? <Spinner size="xsmall" /> : undefined} {...continueProps}>
              {continueButtonText ?? 'Continue'}
            </Button>
          </Box>}
        {showTertiaryButton && <Box className={styles.tertiaryButton} width={adjustedWidth}>
            <Button variant={tertiaryButtonVariant} width="fill" onClick={onTertiaryClick}>
              {tertiaryButtonText}
            </Button>
          </Box>}
      </Box>
    </Box>;
};